import { useStates } from './states';
import sharedService from '../common/services/sharedService';
import { useLayout } from '../layout/composables/layout';

/**
 * Rutas que requieren validación de configuración de empresa
 * Facturas, notas de crédito/débito, guías de remisión y retenciones requieren empresa configurada
 */
const ROUTES_REQUIRING_COMPANY_CONFIG = [
  'salesInvoices',
  'creditNotes',
  'debitNotes',
  'waybillRemissionsEmitted',
  'waybillRemissionsReceived',
  'withholdingElectronics',
];

/**
 * Rutas que requieren validación de productos
 * Todas las vistas que manejan productos en sus operaciones
 */
const ROUTES_REQUIRING_PRODUCTS = [
  'salesInvoices', // Facturas de venta
  'salesNotes', // Notas de crédito (ventas)
  'purchasesNotes', // Notas de débito (compras)
  'proformas', // Proformas
  'purchasesInvoices', // Facturas de compra
  'creditNotes', // Notas de crédito (compras)
  'debitNotes', // Notas de débito (ventas)
];

/**
 * Verifica si una ruta requiere validación de configuración de empresa
 */
function requiresCompanyConfig(routeName: string): boolean {
  if (!routeName) return false;
  return ROUTES_REQUIRING_COMPANY_CONFIG.some((route) => routeName.toLowerCase() === route.toLowerCase());
}

/**
 * Verifica si una ruta requiere validación de productos
 */
function requiresProducts(routeName: string): boolean {
  if (!routeName) return false;
  return ROUTES_REQUIRING_PRODUCTS.some((route) => routeName.toLowerCase() === route.toLowerCase());
}

/**
 * Valida la configuración inicial de la empresa
 * Verifica que exista logo y nombre de empresa
 *
 * @param to - Ruta de destino
 * @returns true si pasa la validación, objeto de redirección si falla
 */
export function validateCompanyConfig(to: any): boolean | { name: string } {
  // Solo validar si la ruta requiere configuración de empresa
  if (!requiresCompanyConfig(to.name)) {
    return true;
  }

  const { userState, onShowConfigCompanyData } = useStates();
  const { setActiveMenuItem } = useLayout();
  const userData = userState.data.value || (null as any);

  // Verificar si falta configuración (solo nameCompany es obligatorio, logoUrl es opcional)
  if (!userData?.nameCompany || userData?.nameCompany === '') {
    setActiveMenuItem('companysettings');
    onShowConfigCompanyData();
    return { name: 'companysettings' };
  }

  return true;
}

/**
 * Valida la existencia de productos en el sistema
 * Verifica que exista al menos un producto registrado
 *
 * @param to - Ruta de destino
 * @returns Promise<true | { name: string }> - true si pasa, objeto de redirección si falla
 */
export async function validateProducts(to: any): Promise<boolean | { name: string }> {
  // Solo validar si la ruta requiere productos
  if (!requiresProducts(to.name)) {
    return true;
  }

  const { companyUserStates, onSetHasProducts, onShowConfigCompanyProducts } = useStates();
  const { setActiveMenuItem } = useLayout();

  // Si ya sabemos que hay productos, permitir acceso
  if (companyUserStates.hasProducts) {
    return true;
  }

  // Verificar en el servidor
  try {
    const response: any = await sharedService.getAllProducts();
    // El backend retorna { products: { totalElements, content, ... }, processingInProgress }
    const hasProducts = response?.products?.totalElements > 0;
    onSetHasProducts(hasProducts);

    if (!hasProducts) {
      setActiveMenuItem('products');
      onShowConfigCompanyProducts();
      return { name: 'products' };
    }

    return true;
  } catch (error) {
    // En caso de error, permitir acceso (no bloquear por error de red)
    console.error('[RedirectionRules] Error validating products:', error);
    return true;
  }
}

/**
 * Aplica todas las reglas de redirección para una ruta
 * Ejecuta las validaciones en orden de prioridad
 *
 * @param to - Ruta de destino
 * @returns Promise<true | { name: string }> - true si pasa todas las validaciones
 */
export async function applyRedirectionRules(to: any): Promise<boolean | { name: string }> {
  // 0. Bloquear rutas que requieren ser Agente de Retención si la compañía no lo es
  if (to?.meta?.requiresWithholdingAgent) {
    const { isWithholdingAgent } = useStates();
    if (!isWithholdingAgent.value) {
      console.warn('[RedirectionRules] Acceso bloqueado: la compañía no es Agente de Retención.', to.name);
      return { name: 'dashboard' };
    }
  }

  // 1. Primero validar configuración de empresa (solo para Sales)
  const companyConfigResult = validateCompanyConfig(to);
  if (companyConfigResult !== true) {
    return companyConfigResult;
  }

  // 2. Luego validar productos (para todas las vistas que los requieren)
  const productsResult = await validateProducts(to);
  if (productsResult !== true) {
    return productsResult;
  }

  return true;
}

/**
 * Limpia el estado de validaciones
 * Útil para resetear después de logout o cambios importantes
 * Nota: La limpieza real se hace directamente en states.ts para evitar dependencias circulares
 */
export function clearRedirectionState(): void {
  // Esta función se mantiene por compatibilidad pero la limpieza real
  // se hace directamente en states.ts (companyUserStates.hasProducts = false)
  console.log('[RedirectionRules] State cleared via states.ts');
}
